import * as React from 'react'

import type {HOC} from '../types'

import withHook from './withHook'

type MaybeCleanUpFn = (() => unknown) | void
type EffectHOC = {
  <P extends object, T1>(getArg0: (props: P) => T1, effect: (arg0: T1) => MaybeCleanUpFn): HOC<P, P>
  <P extends object, T1, T2>(
    getArg0: (props: P) => T1,
    getArg1: (props: P) => T2,
    effect: (arg0: T1, arg1: T2) => MaybeCleanUpFn,
  ): HOC<P, P>
  <P extends object, T1, T2, T3>(
    getArg0: (props: P) => T1,
    getArg1: (props: P) => T2,
    getArg2: (props: P) => T3,
    effect: (arg0: T1, arg1: T2, arg2: T3) => MaybeCleanUpFn,
  ): HOC<P, P>
  <P extends object, T1, T2, T3, T4>(
    getArg0: (props: P) => T1,
    getArg1: (props: P) => T2,
    getArg2: (props: P) => T3,
    getArg3: (props: P) => T4,
    effect: (arg0: T1, arg1: T2, arg2: T3, arg3: T4) => MaybeCleanUpFn,
  ): HOC<P, P>
  <P extends object, T1, T2, T3, T4, T5>(
    getArg0: (props: P) => T1,
    getArg1: (props: P) => T2,
    getArg2: (props: P) => T3,
    getArg3: (props: P) => T4,
    getArg4: (props: P) => T5,
    effect: (arg0: T1, arg1: T2, arg2: T3, arg3: T4, arg4: T5) => MaybeCleanUpFn,
  ): HOC<P, P>
  <P extends object, T1, T2, T3, T4, T5, T6>(
    getArg0: (props: P) => T1,
    getArg1: (props: P) => T2,
    getArg2: (props: P) => T3,
    getArg3: (props: P) => T4,
    getArg4: (props: P) => T5,
    getArg5: (props: P) => T6,
    effect: (arg0: T1, arg1: T2, arg2: T3, arg3: T4, arg4: T5, arg5: T6) => MaybeCleanUpFn,
  ): HOC<P, P>
  <P extends object, T1, T2, T3, T4, T5, T6, T7>(
    getArg0: (props: P) => T1,
    getArg1: (props: P) => T2,
    getArg2: (props: P) => T3,
    getArg3: (props: P) => T4,
    getArg4: (props: P) => T5,
    getArg5: (props: P) => T6,
    getArg6: (props: P) => T7,
    effect: (
      arg0: T1,
      arg1: T2,
      arg2: T3,
      arg3: T4,
      arg4: T5,
      arg5: T6,
      arg6: T7,
    ) => MaybeCleanUpFn,
  ): HOC<P, P>
  // Add more if needed
  <P extends object, T1, T2, T3, T4, T5, T6, T7, T8>(
    getArg0: (props: P) => T1,
    getArg1: (props: P) => T2,
    getArg2: (props: P) => T3,
    getArg3: (props: P) => T4,
    getArg4: (props: P) => T5,
    getArg5: (props: P) => T6,
    getArg6: (props: P) => T7,
    getArg7: (props: P) => T8,
    effect: (
      arg0: T1,
      arg1: T2,
      arg2: T3,
      arg3: T4,
      arg4: T5,
      arg5: T6,
      arg6: T7,
      arg7: T8,
    ) => MaybeCleanUpFn,
  ): HOC<P, P>
}

/* performs effect whenever some of the input selectors returns a changed value */
const withEffect: EffectHOC = (...args: ReadonlyArray<any>) => {
  const [getArg0, getArg1, getArg2, getArg3, getArg4, getArg5, getArg6, getArg7] = args.slice(0, -1)
  const effect = args[args.length - 1]
  return withHook(props => {
    const value0 = getArg0?.(props)
    const value1 = getArg1?.(props)
    const value2 = getArg2?.(props)
    const value3 = getArg3?.(props)
    const value4 = getArg4?.(props)
    const value5 = getArg5?.(props)
    const value6 = getArg6?.(props)
    const value7 = getArg7?.(props)
    React.useEffect(() => {
      const cleanup = effect(value0, value1, value2, value3, value4, value5, value6, value7)
      return typeof cleanup === 'function' ? cleanup : undefined
    }, [value0, value1, value2, value3, value4, value5, value6, value7])
  })
}

export default withEffect
